import { useEffect, useState } from 'react'
import { getTokenFromUrl, decodeSession, isTokenExpired, GAME_LABELS } from './lib/token'
import { useGameSocket } from './hooks/useGameSocket'
import { TopBar } from './components/ui/TopBar'
import { TeenPatti2Card } from './components/games/TeenPatti2Card'
import { AndarBahar } from './components/games/AndarBahar'
import { DragonTiger } from './components/games/DragonTiger'
import type { Session, GameConfig } from './lib/types'
import { AviatorGame } from './components/games/AviatorGame'
import { ChickenRoad } from './components/games/ChickenRoad'
import { ChickenRoadPixi } from './components/games/ChickenRoadPixi'
import { DemoPage } from './components/DemoPage'
import { SteppingStone } from './components/games/SteppingStone'
import { IndianRoadPixi } from './components/games/IndianRoadPixi'
import { SlotGame } from './components/games/SlotGame'
import { StarburstSlot } from './components/games/StarburstSlot'
import { StockGame } from './components/games/StockGame'
import { BalloonGame } from './components/games/BalloonGame'
import { ColorPrediction } from './components/games/ColorPrediction'
import { CricketGame } from './components/games/CricketGame'
import { Crash3DGame } from './components/games/Crash3DGame'
import { RouletteGame } from './components/games/RouletteGame'
import { DiceGame } from './components/games/DiceGame'

function ErrorScreen({ message }: { message: string }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: '100%', background: '#0f172a',
    }}>
      <div style={{ textAlign: 'center', padding: '0 32px' }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
        <p style={{ color: '#e2e8f0', fontWeight: 600, marginBottom: 6 }}>Unable to load game</p>
        <p style={{ color: '#64748b', fontSize: 13, lineHeight: 1.5, maxWidth: 300 }}>{message}</p>
        <button onClick={() => window.location.reload()}
          style={{
            marginTop: 16, padding: '8px 20px', background: '#1e293b',
            border: '1px solid #334155', borderRadius: 8, color: '#94a3b8',
            cursor: 'pointer', fontSize: 13,
          }}>
          Retry
        </button>
      </div>
    </div>
  )
}

function ConnectingScreen() {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: '100%', background: '#0f172a', flexDirection: 'column', gap: 12,
    }}>
      <div style={{
        width: 36, height: 36, border: '3px solid #1e293b',
        borderTop: '3px solid #0ea5e9', borderRadius: '50%',
        animation: 'spin 1s linear infinite',
      }} />
      <p style={{ color: '#475569', fontSize: 13 }}>Connecting to game server…</p>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}

async function fetchGameConfig(gameType: string): Promise<GameConfig | null> {
  try {
    const url = import.meta.env.VITE_GAME_SERVER_URL || 'http://localhost:3002'
    const res = await fetch(`${url}/config/${gameType}`)
    if (!res.ok) return null
    return await res.json()
  } catch {
    return {
      betting_window_ms: 15000,
      min_bet: 1, max_bet: 10000,
      bet_types: {
        player_a:      { label: 'Player A',       odds: 1.98 },
        player_b:      { label: 'Player B',       odds: 1.98 },
        mini_bac_a:    { label: 'Mini Baccarat A', odds: 2.1  },
        mini_bac_b:    { label: 'Mini Baccarat B', odds: 2.1  },
        total_a_under: { label: 'Total A Under',  odds: 14   },
        total_a_over:  { label: 'Total A Over',   odds: 15   },
        total_b_under: { label: 'Total B Under',  odds: 14   },
        total_b_over:  { label: 'Total B Over',   odds: 15   },
        color_plus:    { label: 'Color Plus',     odds: 3    },
        andar:         { label: 'Andar',          odds: 1.9  },
        bahar:         { label: 'Bahar',          odds: 2.0  },
        andar_early:   { label: 'Andar Early',    odds: 3.5  },
        bahar_early:   { label: 'Bahar Early',    odds: 4.5  },
        dragon:        { label: 'Dragon',         odds: 1.95 },
        tiger:         { label: 'Tiger',          odds: 1.95 },
        tie:           { label: 'Tie',            odds: 8    },
        dragon_red:    { label: 'Dragon Red',     odds: 3.75 },
        dragon_black:  { label: 'Dragon Black',   odds: 3.75 },
        tiger_red:     { label: 'Tiger Red',      odds: 3.75 },
        tiger_black:   { label: 'Tiger Black',    odds: 3.75 },
      },
    }
  }
}

function GameShell({ session, token }: { session: Session; token: string }) {
  const [config, setConfig] = useState<GameConfig | null>(null)
  const { connStatus, round, balance, error, placeBet, initialResults, socket } = useGameSocket(token, session.player_id, session.game_type)

  useEffect(() => {
    fetchGameConfig(session.game_type).then(setConfig)
  }, [session.game_type])

  if (error && connStatus === 'error') return <ErrorScreen message={`Connection failed: ${error}`} />
  if (connStatus === 'connecting') return <ConnectingScreen />
  if (session.game_type !== 'aviator' && !config) return <ConnectingScreen />

  const commonProps = { round, config: config!, placeBet, balance, displayName: session.display_name, initialResults }

  const renderGame = () => {
    switch (session.game_type) {
      case 'teen_patti_2card':
      case 'teen_patti_3card':
        return <TeenPatti2Card {...commonProps} />
      case 'andar_bahar':
        return <AndarBahar {...commonProps} />
      case 'dragon_tiger':
        return <DragonTiger {...commonProps} />
      case 'aviator':
        return <AviatorGame
          token={token}
          balance={balance}
          displayName={session.display_name}
          operatorId={session.operator_id}
          playerId={session.player_id}
        />
      case 'chicken_road':
        return <ChickenRoadPixi
          token={token}
          balance={balance}
          displayName={session.display_name}
          operatorId={session.operator_id}
          playerId={session.player_id}
        />
      case 'stepping_stone':
        return <SteppingStone
          token={token}
          balance={balance}
          displayName={session.display_name}
          operatorId={session.operator_id}
          playerId={session.player_id}
        />
      case 'indian_road':
        return <IndianRoadPixi token={token} balance={balance} displayName={session.display_name} operatorId={session.operator_id} playerId={session.player_id} />
      case 'slot':
        return <SlotGame token={token} balance={balance} displayName={session.display_name} operatorId={session.operator_id} playerId={session.player_id} />
      case 'starburst':
        return <StarburstSlot token={token} balance={balance} operatorId={session.operator_id} playerId={session.player_id} />
      case 'dice':
        return <DiceGame token={token} balance={balance} operatorId={session.operator_id} playerId={session.player_id} />
      case 'roulette':
        return <RouletteGame token={token} balance={balance} operatorId={session.operator_id} playerId={session.player_id} />
      case 'crash3d':
        return <Crash3DGame token={token} balance={balance} operatorId={session.operator_id} playerId={session.player_id} />
      case 'cricket':
        return <CricketGame token={token} balance={balance} operatorId={session.operator_id} playerId={session.player_id} />
      case 'colorprediction':
        return <ColorPrediction token={token} balance={balance} operatorId={session.operator_id} playerId={session.player_id} />
      case 'balloon':
        return <BalloonGame token={token} balance={balance} operatorId={session.operator_id} playerId={session.player_id} />
      case 'stock':
        return <StockGame token={token} balance={balance} operatorId={session.operator_id} playerId={session.player_id} />
      default:
        return <ErrorScreen message={`Unknown game: ${session.game_type}`} />
    }
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {renderGame()}
    </div>
  )
}

export default function App() {
  // Demo page — no auth needed
  const pathname = window.location.pathname
  if (pathname === '/demo' || pathname === '/demo/') {
    document.documentElement.style.cssText = 'height:auto!important;overflow-y:auto!important'
    document.body.style.cssText = 'height:auto!important;overflow-y:auto!important'
    const root = document.getElementById('root')
    if (root) root.style.cssText = 'height:auto!important;overflow-y:auto!important'
    return <DemoPage />
  }

  const token   = getTokenFromUrl()
  const session = token ? decodeSession(token) : null

  if (!token || !session) {
    return <div style={{ height: '100%' }}>
      <ErrorScreen message="No session token. Launch this game via the operator API." />
    </div>
  }
  if (isTokenExpired(session)) {
    return <div style={{ height: '100%' }}>
      <ErrorScreen message="Session expired. Please re-launch the game." />
    </div>
  }

  return <GameShell session={session} token={token} />
}
